import React, { useState } from "react";
import { useMenuContext } from "context/MenuContext";
import Slider from "components/Slider";
import classnames from "classnames";
import { getFormatedPrice } from "helpers";
import { getHighAndLowestPrice } from "helpers/productHelpers";
import {
  ProductWithRelations,
} from "Types";
import {
  ProductVariantOption,
} from ".prisma/client";
import VariantSel from "components/VariantSelection";
import { getTranslation } from "dictionary";
import { useUserContext } from "context/UserContext";
import { useProduct } from "hooks/useProduct";
import { useStylesContext } from "context/StylesContext";
import getImageUrl from "helpers/getImageUrl";
import Modal from "components/Modal";
import CartCount from "components/CartCount";

interface Props {
  item: ProductWithRelations;
}

const MenuAccordion: React.FC<Props> = ({ item }) => {
  const { language } = useUserContext();
  const {
    currency_symbol,
    features,
  } = useMenuContext();
  const price = getHighAndLowestPrice(item);
  const { innerWidth } = useStylesContext().windowSize;

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { variantSelection, handleOptionSelection, handleAddToCart, count, changeCount } =
    useProduct(item, features);

  const handleOpenModal = () =>
    innerWidth < 640 ? setIsModalOpen(true) : null;

  const slides = () =>
    item.images?.map((image) => {
      return (
        <div
          key={image.image_id}
          id={image.image_id.toString()}
          className="keen-slider__slide min-w-auto"
        >
          <img
            onClick={handleOpenModal}
            src={getImageUrl(image.url, 300)}
            alt={item.title + " imagen " + image.position}
            width={390}
            height={390}
            className="object-cover w-full aspect-square rounded-lg max-w-[180px] object-center"
          />
        </div>
      );
    });

  const perViewSlides =
    innerWidth <= 460
      ? 2
      : innerWidth > 460 && innerWidth <= 640
      ? 3
      : innerWidth > 640 && innerWidth < 768
      ? 4
      : 2;
  const typeSlides = innerWidth <= 460 ? "dots" 
  : innerWidth > 460 && innerWidth < 768
  ? ""
  : "dots";

  return (
    <details className="py-4 border-b border-gray-200">
      <summary className="flex justify-between">
        <span className="text-lg">{item.title}</span>
        <div className="flex flex-wrap gap-x-2 items-center justify-end">
          <span
            className={classnames("font-light ", {
              "text-lg": !item.price_promotional,
              "line-through text-gray-500 text-lg": item.price_promotional,
            })}
          >
            {price.uniquePrice ? (
              getFormatedPrice(item.price, currency_symbol)
            ) : (
              <span>
                {getFormatedPrice(price.lowestPrice || 0, currency_symbol)} -{" "}
                {getFormatedPrice(price.highestPrice || 0, currency_symbol)}
              </span>
            )}
          </span>
          {item.price_promotional && (
            <span className="font-light text-lg">
              {price.uniquePrice ? (
                getFormatedPrice(item.price_promotional, currency_symbol)
              ) : (
                <span>
                  {getFormatedPrice(
                    price.lowestPromotionalPrice || 0,
                    currency_symbol
                  )}{" "}
                  -{" "}
                  {getFormatedPrice(
                    price.highestPromotionalPrice || 0,
                    currency_symbol
                  )}
                </span>
              )}
            </span>
          )}
        </div>
      </summary>
      <div>
        {item.status === "not_available" && (
          <p className="text-sm font-light mb-4 underline underline-offset-2">
            {getTranslation("menu_product", "PRODUCT_NOT_AVAILABLE", language)}
          </p>
        )}
        {item.description && (
          <p className="font-light text-base my-4">{item.description}</p>
        )}

        {item.images[0] ? (
          <div className="my-4">
            <Slider
              slides={slides()}
              onSlideChange={(slider) =>
                setCurrentSlide(slider.track.details.rel)
              }
              perView={perViewSlides}
              spacing={8}
              type={typeSlides}
            />
          </div>
        ) : null}

        {item.status !== "not_available" &&
          item.variants?.map((variant) => (
            <VariantSel
              key={variant.id}
              selectedOption={variantSelection[variant.id]}
              variant={variant}
              handleSelection={(option: ProductVariantOption) =>
                handleOptionSelection(variant, option)
              }
            />
          ))}

        {item.status === "not_available" || features === "CATALOGO" ? null : (
          <div className="mt-4 flex gap-3">
            <CartCount
              size="big"
              changeCount={changeCount}
              count={count}
            />
            <button
              onClick={handleAddToCart}
              aria-label="Add to cart"
              className="px-3 py-1.5 text-sm border border-black tracking-wider leading-tight font-medium uppercase rounded-md hover:bg-gray-100"
            >
              {getTranslation("menu_product", "ADD", language)}
            </button>
          </div>
        )}
      </div>
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className="h-4/6 mb-8" onClick={() => setIsModalOpen(false)}>
          <Slider
            initialSlide={currentSlide}
            slides={item.images?.map((image) => (
              <div
                key={image.image_id}
                className="keen-slider__slide px-2 m-auto"
              >
                <img
                  src={getImageUrl(image.url, 1000)}
                  alt={image.filename + "image"}
                  className="w-full rounded-lg object-contain"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            ))}
          />
        </div>
      </Modal>
    </details>
  );
};

export default MenuAccordion;
